import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Star,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Clock,
  ExternalLink,
  Heart,
  MessageCircle,
  Share,
  MoreVertical,
  ChevronRight,
  Award,
  Target,
  Briefcase
} from 'lucide-react';

const NativeAdCard = ({ 
  adType = 'feed', // 'feed', 'discovery', 'group-suggestion'
  placement = 'timeline',
  onAdClick,
  onAdInteraction
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [liked, setLiked] = useState(false);
  const [interactions, setInteractions] = useState({ likes: 0, comments: 0, shares: 0 });

  // Native ad templates that blend with app content
  const nativeAds = {
    feed: [
      {
        id: 'native-bank-1',
        type: 'group-suggestion',
        title: 'Groupe Épargne Plus - Equity Bank',
        description: 'Rejoignez plus de 1,200 épargnants et bénéficiez de taux préférentiels sur vos contributions. Programme certifié par la Banque Centrale du Kenya.',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDMwMCAxNjAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMTYwIiByeD0iMTIiIGZpbGw9IiNmMGY5ZmYiLz48Y2lyY2xlIGN4PSI3NSIgY3k9IjgwIiByPSIzMCIgZmlsbD0iIzA2YjZkNCIvPjx0ZXh0IHg9Ijc1IiB5PSI4OCIgZm9udC1zaXplPSIxOCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5FPC90ZXh0Pjx0ZXh0IHg9IjEyMCIgeT0iNzAiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSIjMWY0OTVhIj5Fcargbmhl4KICUGx1czwvdGV4dD48dGV4dCB4PSIxMjAiIHk9Ijk1IiBmb250LXNpemU9IjEyIiBmaWxsPSIjNjM2NjZkIj7CpHM4JBQnGh5w6nHXKJO1IGF1vbZ1ZWwgPC90ZXh0Pjwvc3ZnPg==',
        brand: 'Equity Bank',
        memberCount: '1,247',
        avgContribution: 'KSh 5,000',
        successRate: '98%',
        category: 'Banque Partenaire',
        verified: true,
        cta: 'Rejoindre le Groupe',
        benefits: ['Taux préférentiels', 'Support 24/7', 'Assurance incluse'],
        testimonial: '"Mon épargne a doublé en 12 mois grâce à leurs conseils" - Marie K.',
        sponsor: true
      }
    ],
    discovery: [
      {
        id: 'native-fintech-1',
        type: 'service-promo',
        title: 'Nouvelle Fonctionnalité: Investissement Intelligent',
        description: 'Découvrez notre nouveau service d\'investissement automatisé qui optimise vos contributions selon les tendances du marché.',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDMwMCAxNjAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMTYwIiByeD0iMTIiIGZpbGw9IiNmM2Y0ZjYiLz48cGF0aCBkPSJtNTAgODAgNDAtMjAgNDAgMjAgNDAtMjAgNDAgMjAiIHN0cm9rZT0iIzEwYjk4MSIgc3Ryb2tlLXdpZHRoPSI0IiBmaWxsPSJub25lIi8+PGNpcmNsZSBjeD0iNzUiIGN5PSI1MCIgcj0iOCIgZmlsbD0iIzEwYjk4MSIvPjxjaXJjbGUgY3g9IjEzNSIgY3k9IjcwIiByPSI4IiBmaWxsPSIjMTBiOTgxIi8+PGNpcmNsZSBjeD0iMTk1IiBjeT0iNTAiIHI9IjgiIGZpbGw9IiMxMGI5ODEiLz48dGV4dCB4PSIxNTAiIHk9IjEyNSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiMxZjQ5NWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkludmVzdGlzc2VtZW50IEludGVsbGlnZW50PC90ZXh0Pjwvc3ZnPg==',
        brand: 'Likico Pro',
        roi: '+15%',
        minAmount: 'KSh 1,000',
        timeframe: '6 mois',
        category: 'Fonctionnalité Premium',
        verified: false,
        cta: 'Essayer Gratuitement',
        benefits: ['Algorithme IA', 'Rendements optimisés', 'Risque calculé'],
        offer: 'Premiers 30 jours gratuits',
        sponsor: true
      }
    ],
    groupSuggestion: [
      {
        id: 'native-group-1',
        type: 'group-recommendation',
        title: 'Groupe recommandé pour vous',
        description: 'Basé sur votre profil et vos préférences d\'épargne',
        groupName: 'Entrepreneures Digitales',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDMwMCAxNjAiIGZpbGw9Im5vbmUiPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMTYwIiByeD0iMTIiIGZpbGw9IiNmZGY0ZmYiLz48Y2lyY2xlIGN4PSI5MCIgY3k9IjYwIiByPSIyMCIgZmlsbD0iIzhhNWNmNiIvPjxjaXJjbGUgY3g9IjEzMCIgY3k9IjgwIiByPSIyMCIgZmlsbD0iIzhjNWNmNiIvPjxjaXJjbGUgY3g9IjE3MCIgY3k9IjYwIiByPSIyMCIgZmlsbD0iIzhjNWNmNiIvPjx0ZXh0IHg9IjE1MCIgeT0iMTMwIiBmb250LXNpemU9IjE0IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0iIzFmNDk1YSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+RW50cmVwcmVuZXVyZXMgRGlnaXRhbGVzPC90ZXh0Pjx0ZXh0IHg9IjkwIiB5PSI2NyIgZm9udC1zaXplPSIxMCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5BQTK0L3RleHQ+PHRleHQgeD0iMTMwIiB5PSI4NyIgZm9udC1zaXplPSIxMCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5CQjwvdGV4dD48dGV4dCB4PSIxNzAiIHk9IjY3IiBmb250LXNpemU9IjEwIiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkNDPC90ZXh0Pjwvc3ZnPg==',
        memberCount: '89',
        avgContribution: 'KSh 7,500',
        frequency: 'Mensuel',
        compatibility: '92%',
        category: 'Groupe Suggéré',
        verified: true,
        cta: 'Voir le Groupe',
        tags: ['Tech', 'Femmes', 'Business'],
        adminName: 'Grace Wanjiku',
        sponsor: true
      }
    ]
  };

  useEffect(() => {
    const ads = nativeAds[adType] || nativeAds.feed;
    if (ads.length > 0) {
      const selectedAd = ads[Math.floor(Math.random() * ads.length)];
      setCurrentAd(selectedAd);
      setInteractions({
        likes: Math.floor(Math.random() * 50) + 10,
        comments: Math.floor(Math.random() * 20) + 3,
        shares: Math.floor(Math.random() * 15) + 1
      });
    }
  }, [adType]);

  const handleLike = () => {
    setLiked(!liked);
    setInteractions(prev => ({
      ...prev,
      likes: liked ? prev.likes - 1 : prev.likes + 1
    }));
    
    if (onAdInteraction) {
      onAdInteraction(currentAd.id, 'like', !liked);
    }
  };

  const handleShare = () => {
    setInteractions(prev => ({
      ...prev,
      shares: prev.shares + 1
    }));
    
    if (onAdInteraction) {
      onAdInteraction(currentAd.id, 'share');
    }
  };

  const handleClick = () => {
    if (onAdClick) {
      onAdClick(currentAd);
    }
  };

  const handleCtaClick = (e) => {
    e.stopPropagation();
    if (onAdClick) {
      onAdClick(currentAd, 'cta');
    }
  };

  if (!currentAd) return null;

  return (
    <Card 
      className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={handleClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <span className="text-sm font-medium text-gray-900">{currentAd.brand}</span>
              {currentAd.verified && (
                <Shield className="w-4 h-4 text-blue-500" />
              )}
            </div>
            <span className="text-xs text-gray-500 px-2 py-1 bg-gray-50 rounded-full border">
              Sponsorisé
            </span>
          </div>
          <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Main Content */}
        <div>
          <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
            {currentAd.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-3">
            {currentAd.description}
          </p>
        </div>

        {/* Ad Image/Visual */}
        {currentAd.image && (
          <div className="rounded-lg overflow-hidden border border-gray-100">
            <img 
              src={currentAd.image} 
              alt={currentAd.title}
              className="w-full h-40 object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}

        {/* Stats/Features based on ad type */}
        {currentAd.type === 'group-suggestion' && (
          <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Users className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-sm font-semibold text-gray-900">{currentAd.memberCount}</p>
              <p className="text-xs text-gray-500">Membres</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-sm font-semibold text-gray-900">{currentAd.avgContribution}</p>
              <p className="text-xs text-gray-500">Moyenne</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Star className="w-4 h-4 text-yellow-500" />
              </div>
              <p className="text-sm font-semibold text-gray-900">{currentAd.successRate}</p>
              <p className="text-xs text-gray-500">Succès</p>
            </div>
          </div>
        )}

        {currentAd.type === 'service-promo' && (
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-100">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700">ROI {currentAd.roi}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">{currentAd.timeframe}</span>
              </div>
            </div>
            {currentAd.offer && (
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                {currentAd.offer}
              </span>
            )}
          </div>
        )}

        {currentAd.type === 'group-recommendation' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-gray-700">Compatibilité</span>
              </div>
              <span className="text-sm font-semibold text-purple-600">{currentAd.compatibility}</span>
            </div>
            
            {currentAd.tags && (
              <div className="flex flex-wrap gap-2">
                {currentAd.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Benefits */}
        {currentAd.benefits && (
          <div className="space-y-2">
            {currentAd.benefits.slice(0, 3).map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Zap className="w-3 h-3 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-600">{benefit}</span>
              </div>
            ))}
          </div>
        )}

        {/* Testimonial */}
        {currentAd.testimonial && (
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-sm italic text-blue-800">{currentAd.testimonial}</p>
          </div>
        )}

        {/* CTA Button */}
        <div className="pt-2">
          <button
            onClick={handleCtaClick}
            className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 group"
          >
            <span>{currentAd.cta}</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Social Interactions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center space-x-6">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-2 transition-colors ${
                liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              <span className="text-sm">{interactions.likes}</span>
            </button>
            
            <button className="flex items-center space-x-2 text-gray-500 hover:text-blue-500 transition-colors">
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm">{interactions.comments}</span>
            </button>
            
            <button 
              onClick={handleShare}
              className="flex items-center space-x-2 text-gray-500 hover:text-green-500 transition-colors"
            >
              <Share className="w-4 h-4" />
              <span className="text-sm">{interactions.shares}</span>
            </button>
          </div>
          
          <span className="text-xs text-gray-400">{currentAd.category}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default NativeAdCard;